import React from "react";
import Base from "../Layouts/Base";
import Footer from "../Components/Footer";

const Privacy = () => (
  <Base>
    <div className="container termsWrapper my-3">
      <h3 className="text-center mb-4">Privacy Policy</h3>
      <p className="text-center">Effective date: October 1, 2026</p>

      <section className="my-3">
        <h4>Who we are and what this covers</h4>
        <p>
          Beach Collective (Beach Collective LLC, “we”, “us”) runs a rewards
          program for beach and ocean cleanups. You photograph trash you’ve
          picked up, we verify the cleanup, and you earn points that can be
          converted into $BEACH tokens.
        </p>
        <p>
          This policy explains what information we collect and how we use it
          when you use:
        </p>
        <ul className="terms-list">
          <li>
            our websites, including beachcollective.io, the cleanup leaderboard
            and the sign-up and submission page;
          </li>
          <li>our Telegram bot (@beachcollectivebot); and</li>
          <li>
            our X account and bot (@bcollective_llc), when you mention or
            message it.
          </li>
        </ul>
        <p>Our online store has its own privacy policy, which applies to purchases.</p>
      </section>

      <section className="my-3">
        <h4>Information we collect</h4>
        <p>
          We collect what you give us, what our verification creates, and basic
          technical data needed to keep the service secure.
        </p>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">What</th>
                <th scope="col">Details</th>
                <th scope="col">When</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Account details</th>
                <td>Email address, the username you choose, your referral code and who invited you</td>
                <td>When you sign up or sign in on the web</td>
              </tr>
              <tr>
                <th scope="row">Sign-in with X or Telegram</th>
                <td>Your username, account ID and display name, and your email if X shares it with us</td>
                <td>When you sign in with, link or use those accounts</td>
              </tr>
              <tr>
                <th scope="row">Wallet address</th>
                <td>Your public wallet address and a signed message proving you own it. We never ask for or receive private keys or seed phrases</td>
                <td>When you sign in with a wallet or add a payout wallet</td>
              </tr>
              <tr>
                <th scope="row">Cleanup photos</th>
                <td>The photo and any metadata your phone saved with it, which can include the time and location it was taken. We also store a fingerprint of each image to catch duplicates</td>
                <td>When you submit a cleanup</td>
              </tr>
              <tr>
                <th scope="row">Cleanup results</th>
                <td>Estimated weight, items identified, verification result, points, badges, streaks, claims and corrections</td>
                <td>After each submission</td>
              </tr>
              <tr>
                <th scope="row">Bot conversations</th>
                <td>Messages and voice notes you send our bots, and short summaries we keep so the bot remembers context</td>
                <td>When you message our bots</td>
              </tr>
              <tr>
                <th scope="row">Technical and security data</th>
                <td>IP address, browser and device type, sign-in records and a bot-protection check (Cloudflare Turnstile)</td>
                <td>When you use our sites</td>
              </tr>
              <tr>
                <th scope="row">On your device</th>
                <td>Your sign-in session, language choice, a pending sign-in email and an invite code are saved in your browser’s storage. Cloudflare’s bot check may also store its own data</td>
                <td>When you use the sign-up page</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          We don’t sell your personal information, and we don’t use advertising
          trackers on the sign-up page.
        </p>
      </section>

      <section className="my-3">
        <h4>Location information</h4>
        <p>
          When you submit a cleanup, we may collect location information to
          verify the cleanup and to report on where cleanups happen. This can
          include:
        </p>
        <ul className="terms-list">
          <li>
            <strong>Photo location data.</strong> Many phones store the place a
            photo was taken inside the image file. If your photo includes this
            information, we read it when you submit.
          </li>
          <li>
            <strong>Device location.</strong> If you allow it in your browser,
            we may record your approximate location at the moment you submit.
          </li>
          <li>
            <strong>Beach or event details you provide,</strong> such as the
            beach you select or an event code.
          </li>
        </ul>
        <p>
          Location is optional. You can submit a cleanup without sharing it,
          and your submission and rewards are not affected.
        </p>
        <p>
          <strong>How we use and store it.</strong> We use location to match
          your cleanup to a beach, to help detect fraudulent submissions, and
          to produce impact reports, such as total cleanups at a beach or at an
          event. Precise coordinates and your original photo files are stored
          privately and are not shown publicly. The name of the beach associated
          with a cleanup may appear alongside your submission on the leaderboard.
        </p>
        <p>
          <strong>Events and partners.</strong> If you join a sponsored or
          organized cleanup event, the organizer may receive a report of the
          event’s results. These reports include totals such as weight, items
          collected and number of participants. They do not include your precise
          location, and they include your name only if you have agreed to be
          named.
        </p>
        <p>
          <strong>Your choices.</strong> You can turn off location access for
          your camera or browser in your device settings at any time. To request
          deletion of location data associated with your submissions, contact{" "}
          <a className="text-dark" href="mailto:hello@beachtoken.io">
            hello@beachtoken.io
          </a>
          .
        </p>
      </section>

      <section className="my-3">
        <h4>How we use it</h4>
        <p>We use your information to run the rewards program and keep it fair:</p>
        <ul className="terms-list">
          <li>
            <strong>Verify cleanups.</strong> Photos are analyzed by automated
            tools, including AI image analysis, to identify trash, estimate
            weight and check for reused or duplicate photos.
          </li>
          <li>
            <strong>Award and pay rewards.</strong> We calculate points,
            streaks, badges and referral rewards, and send $BEACH to your payout
            wallet when you claim.
          </li>
          <li>
            <strong>Run your account.</strong> We sign you in, link your web,
            Telegram and X accounts so your history is in one place, and show
            your dashboard.
          </li>
          <li>
            <strong>Prevent fraud and abuse.</strong> We check for duplicate
            photos, throwaway accounts, self-referrals and automated sign-ups,
            and we may limit or review accounts that look suspicious.
          </li>
          <li>
            <strong>Communicate with you.</strong> We send sign-in links and
            codes, and the bots reply to your submissions and messages.
          </li>
          <li>
            <strong>Improve the program.</strong> We look at overall usage and
            cleanup totals, for example total weight collected, to improve the
            service and report our impact.
          </li>
        </ul>
        <p>
          We rely on your agreement to our Terms to provide the service, and on
          our legitimate interest in keeping the program secure and fair. Where
          the law requires consent, we ask for it.
        </p>
      </section>

      <section className="my-3">
        <h4>What is public</h4>
        <p>Some information is visible to others by design:</p>
        <ul className="terms-list">
          <li>
            <strong>Leaderboard.</strong> Your display name, total weight
            collected, points and rank are shown on the public leaderboard. Pick
            a display name you’re comfortable sharing.
          </li>
          <li>
            <strong>Referrals.</strong> People who invited you can see your
            display name and how much you’ve collected.
          </li>
          <li>
            <strong>X and Telegram.</strong> Posts and replies on X are public.
            Messages in Telegram groups are visible to the group. Our bots’
            replies to you appear in the same place.
          </li>
          <li>
            Photos you submit on our website are stored privately and shown only
            to you on your dashboard, unless you share them using the share
            buttons, or we feature them as described in our Terms (credited to
            your display name; you can ask us to stop at any time).
          </li>
          <li>
            <strong>Blockchain.</strong> Token payouts are recorded on a public
            blockchain. Your payout wallet address and the amounts sent to it
            are permanently public and can’t be deleted by anyone, including us.
          </li>
        </ul>
        <p>
          When you share a cleanup to X, Telegram, Facebook, WhatsApp or another
          app, you post it from your own account, under that app’s privacy
          policy.
        </p>
      </section>

      <section className="my-3">
        <h4>Who we share it with</h4>
        <p>
          We share information only with companies that help us run the program,
          and only what each one needs:
        </p>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Provider</th>
                <th scope="col">What they do for us</th>
                <th scope="col">What they handle</th>
              </tr>
            </thead>
            <tbody>
              <tr><th scope="row">Supabase</th><td>Database, sign-in and photo storage</td><td>Account details, cleanup data, photos</td></tr>
              <tr><th scope="row">Cloudflare</th><td>Website hosting and bot protection</td><td>Technical and security data</td></tr>
              <tr><th scope="row">OpenAI</th><td>Photo analysis, bot replies and voice transcription</td><td>Photos, bot messages and voice notes</td></tr>
              <tr><th scope="row">n8n</th><td>Automation that runs our bots and cleanup verification</td><td>Data passing between our systems</td></tr>
              <tr><th scope="row">Telegram and X</th><td>Messaging, posting and sign-in</td><td>What you send through them, sign-in details</td></tr>
              <tr><th scope="row">Resend</th><td>Sending sign-in links and codes</td><td>Email address</td></tr>
              <tr><th scope="row">Google</th><td>Sign-in with Google</td><td>Name, email address</td></tr>
              <tr><th scope="row">Blockchain infrastructure (Chainstack)</th><td>Sending $BEACH payouts</td><td>Wallet address and payout amount</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          When you ask our bots a general question, they may send your message
          to public search, translation, weather, news or price services to look
          up an answer.
        </p>
        <p>
          We may also share information if the law requires it, to protect
          people’s safety or the integrity of the program, or as part of a
          merger, sale or reorganization of our business, in which case this
          policy continues to apply.
        </p>
        <p>Some of these providers process data in the United States and other countries.</p>
      </section>

      <section className="my-3">
        <h4>How long we keep it</h4>
        <p>
          We keep your account, cleanup history and photos while your account is
          open, so your points, badges and leaderboard totals stay accurate.
          Photo fingerprints from deleted accounts may be kept to stop the same
          photos being resubmitted. Records of token payouts are kept as long as
          tax and accounting rules require. Server logs, which include IP
          addresses, are kept for about 7 days. Sign-in records (IP address and
          browser) are kept until you sign out or the session expires. Bot
          conversation summaries are kept while your account is open.
        </p>
      </section>

      <section className="my-3">
        <h4>Your choices and rights</h4>
        <ul className="terms-list">
          <li>
            <strong>See or download your data.</strong> Your dashboard shows
            your submissions, points and wallet. For a full copy, contact us.
          </li>
          <li>
            <strong>Correct it.</strong> Change your display name, and fix a
            cleanup’s weight with the /correct command in the bot.
          </li>
          <li>
            <strong>Delete your account.</strong> Contact us and we’ll delete
            your account and photos, except what we must keep as described
            above. Data already on a blockchain can’t be removed.
          </li>
          <li>
            <strong>Unlink accounts.</strong> You can unlink your Telegram and
            X accounts.
          </li>
        </ul>
        <p>
          Depending on where you live, including California and the EU/UK, you
          may have additional rights, such as to object to certain uses or to
          complain to a data protection authority. We won’t treat you
          differently for using them.
        </p>
      </section>

      <section className="my-3">
        <h4>Age limit</h4>
        <p>
          The program is for people 18 and older. We don’t knowingly collect
          information from anyone under 18. If you believe a minor has signed
          up, contact us and we’ll delete the account.
        </p>
      </section>

      <section className="my-3">
        <h4>Security</h4>
        <p>
          We protect your information with encrypted connections, private photo
          storage, access controls and bot protection on sign-in. No system is
          perfectly secure, so keep your email account and wallet safe. We will
          never ask for your private keys, seed phrase or passwords.
        </p>
      </section>

      <section className="my-3">
        <h4>Changes to this policy</h4>
        <p>
          If we make important changes, we’ll update the effective date and
          post a notice on the site or through the bots before they take effect.
        </p>
      </section>

      <section className="my-3">
        <h4>Contact</h4>
        <p>
          Questions or requests:{" "}
          <a className="text-dark" href="mailto:hello@beachtoken.io">
            hello@beachtoken.io
          </a>
          .
        </p>
      </section>
    </div>
    <section className="footer-shape">
      <img src="/images/footer-top-shape.png" alt="" />
    </section>
    <Footer />
  </Base>
);

export default Privacy;
